import React, {useEffect} from "react";
import {Route, Routes, useLocation, useNavigate} from "react-router-dom";
import Login from "../../onboarding/pages/Login";
import PlatformApp from "./PlatformApp";
import {Loading} from "./Loading";
import {
    buildUrl,
    FORGOT_PASSWORD_EMAIL,
    FORGOT_PASSWORD_RESET,
    LOADING,
    LOGIN, PAY_CADDIE, PAYMENT_CONFIRMATION,
    PLATFORM_HOME,
    REGISTER_CREATE_PROFILE,
    REGISTER_CREDENTIALS,
    REGISTER_EMAIL_CONFIRM
} from "../../../services/PageUtils";
import {useDispatch} from "react-redux";
import {AppDispatch, startAppListening} from "../../../services/store/store";
import {Unsubscribe} from "@reduxjs/toolkit";
import {setupClubListeners} from "../../../services/store/reducers/clubs/listeners";
import useColourScheme from "../../../hooks/useColourScheme";
import ResetPassword from "../../onboarding/pages/ResetPassword";
import ForgotPasswordEmailConfirmation from "../../onboarding/pages/ForgotPasswordEmailConfirmation";
import RegisterCredentials from "../../onboarding/pages/RegisterCredentials";
import RegisterEmailConfirm from "../../onboarding/pages/RegisterEmailConfirm";
import RegisterCreateProfile from "../../onboarding/pages/RegisterCreateProfile";
import {refreshSession} from "../../../services/store/reducers/session/asyncActions";
import {handicaddieSelector} from "../../../services/store/asyncThunk";
import {handleLogOut, SessionState} from "../../../services/store/reducers/session/slice";
import {setupSessionListeners} from "../../../services/store/reducers/session/listeners";
import {handleClubIdFromUrl} from "../../../services/store/reducers/clubs/slice";
import {setupPostRoundListeners} from "../../../services/store/reducers/postRound/listeners";
import {handleCaddieIdFromUrl, handlePaymentIdFromUrl} from "../../../services/store/reducers/postRound/slice";

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const App = () => {

    const navigate = useNavigate();

    // this is required to initialise the theming!
    useColourScheme();

    const dispatch = useDispatch<AppDispatch>();
    const { sessionState } = handicaddieSelector(state => state.session)
    const { clubIdFromUrl } = handicaddieSelector(state => state.clubs)
    const { caddieId, paymentId } = handicaddieSelector(state => state.postRound.urlParams)

    let query = useQuery();
    let urlClubId = query.get("clubId");
    let urlPaymentId = query.get("paymentId");
    let urlCaddieId = query.get("caddieId");

    useEffect(() => {
        if (!urlClubId) {
            return;
        }

        dispatch(handleClubIdFromUrl(urlClubId))
    }, [urlClubId]);

    useEffect(() => {
        if (!urlPaymentId) {
            return;
        }

        dispatch(handlePaymentIdFromUrl(urlPaymentId))
    }, [urlPaymentId]);

    useEffect(() => {
        if (!urlCaddieId) {
            return;
        }

        dispatch(handleCaddieIdFromUrl(urlCaddieId))
    }, [urlCaddieId]);

    useEffect(() => {
        dispatch(refreshSession())
    }, [])

    useEffect(() => {
        const subscriptions: Unsubscribe[] = [
            setupClubListeners(startAppListening),
            setupPostRoundListeners(startAppListening),
            setupSessionListeners(startAppListening),
        ]

        return () => subscriptions.forEach((unsubscribe) => unsubscribe())
    }, [])

    useEffect(() => {
        switch (sessionState) {
            case SessionState.LOADING:
                return;
            case SessionState.REGISTERING_RESETTING:
                return;
            case SessionState.INACTIVE:
                navigate(LOGIN);
                return;
            case SessionState.NO_PROFILE:
                navigate(REGISTER_CREATE_PROFILE);
                return;
            default:
                const isOnPlatformRoute = window.location.pathname.startsWith('/platform/');
                console.log('navigating to platform')
                if (!isOnPlatformRoute) {
                    if (clubIdFromUrl && caddieId && paymentId) {
                        console.log('App.tsx navigating to PAYMENT_CONFIRMATION')
                        navigate(buildUrl(PAYMENT_CONFIRMATION))
                    } else if (clubIdFromUrl && caddieId) {
                        console.log('App.tsx navigating to PAY_CADDIE')
                        navigate(buildUrl(PAY_CADDIE))
                    } else {
                        console.log('App.tsx navigating to PLATFORM_HOME')
                        navigate(buildUrl(PLATFORM_HOME)); // or whatever default you want
                    }
                }
                console.log('navigation called & completed...')
                return;
        }
    }, [sessionState]);

    const logOut = () => {
        dispatch(handleLogOut())
        navigate(LOGIN)
    }

    return (
        <div style={{height: "100%"}}>
            <Routes>
                <Route path={LOGIN} element={<Login />} />
                <Route path={LOADING} element={<Loading />}/>
                <Route path={FORGOT_PASSWORD_EMAIL} element={<ForgotPasswordEmailConfirmation />}/>
                <Route path={FORGOT_PASSWORD_RESET} element={<ResetPassword />} />
                <Route path={REGISTER_CREDENTIALS} element={<RegisterCredentials />} />
                <Route path={REGISTER_EMAIL_CONFIRM} element={<RegisterEmailConfirm />} />
                <Route path={REGISTER_CREATE_PROFILE} element={<RegisterCreateProfile />} />
                <Route path="/platform/*" element={<PlatformApp logOut={logOut} />} />
                {/*<Route index element={<PlatformApp logOut={logOut} />} />*/}
                <Route index element={<PlatformApp logOut={logOut} />} />
            </Routes>
        </div>
    );
};

export default App;
